import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  extractUniqueFunds,
  groupAllStatusByRegulationInSection,
} from '../dashboard-utils';
import {
  ComplianceDataResponse,
  PieStatusKey,
  RegulationId,
} from '../types/dashboard.types';
import ComplianceTable from './ComplianceTable';
import {
  slideInLeft,
  slideInRight,
} from '../../../ui/animations/layout.animations';
import { ChartViewProvider, useChartView } from '../DashboardContext';
import ComplianceBarChart from './ComplianceBarChart';
import ChartToolbar from './ChartToolbar';
import {
  extractAllDates,
  extractAllDatesFromChartData,
  filterComplianceDataByRegulationId,
  processChartEntry,
  processChartEntryFromChartData,
} from '../utils/chart.utils';
import { SimplifiedSection } from '../../../../types/server-data/ClientConfig';
import LoadingSpinner from '../../../ui/LoadingSpinner';
import { useIsActiveRangeLoading } from '../utils/date.utils';
import { useIsFetching } from '@tanstack/react-query';

interface ChartViewSectionProps {
  daysInScope: number;
  dataInDateRange: ComplianceDataResponse;
  regulation: RegulationId;
  allRegulations?: RegulationId[];
  pageSize?: number; // optional page size for table, defaults to 10
  selectedSubcategory?: string | null; // optional subcategory to filter by
  activeCustomRange?: { startDate: string; endDate: string } | null;
  sections?: SimplifiedSection[] | null;
  entityLabel?: string;
}

const ChartViewSectionComponent: React.FC<ChartViewSectionProps> = ({
  regulation,
  daysInScope,
  dataInDateRange,
  pageSize = 10,
  selectedSubcategory,
  activeCustomRange,
  sections,
  entityLabel = 'ManCo',
}) => {
  const { fundIdCtx } = useChartView();

  const [fundFilters, setFundFilters] = useState<string[]>([]);
  const [subCategoryFilters, setSubCategoryFilters] = useState<string[]>([]);
  const [hasUserAdjustedSubFilters, setHasUserAdjustedSubFilters] =
    useState<boolean>(false);
  const prevDataRef = useRef<typeof dataInDateRange | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filteredData = useMemo(
    () => filterComplianceDataByRegulationId(dataInDateRange, regulation?.id),
    [dataInDateRange, regulation?.id],
  );

  // Get regulation data and chart data directly from the API response
  const regulationData = Object.values(filteredData.regulation_breakdown)[0];
  const chartData = regulationData?.chart_data || [];

  const [subCategoriesFails, setSubCategoriesFails] = useState<string[]>([]);

  // Only memoize expensive computations
  const allDates = useMemo(
    () => extractAllDatesFromChartData(chartData),
    [chartData],
  );
  const allFunds = useMemo(() => {
    const tableData = regulationData?.table_data || [];
    return tableData.map((fund) => ({
      fund_id: fund.fund_id,
      fund_name: fund.fund_name,
      fund_name_full: fund.fund_name_full,
    }));
  }, [regulationData]);
  const [statusFilters, setStatusFilters] = useState<PieStatusKey[]>([
    'Fail',
    'Alert',
  ]);

  // Initialize subCategoriesFails when regulationData changes
  useEffect(() => {
    if (regulationData?.subcategories) {
      setSubCategoriesFails(regulationData.subcategories);
    }
  }, [regulationData]);

  // Apply selectedSubcategory only as an initial filter, allow user to override
  useEffect(() => {
    if (selectedSubcategory && !hasUserAdjustedSubFilters) {
      const normalize = (s: string) =>
        s.toLowerCase().replace(/[^a-z0-9]/gi, '');
      const target = normalize(selectedSubcategory);

      // Get all subcategories from regulationData
      const keys = regulationData?.subcategories || [];

      // Try exact normalized match, then fallback to contains
      const exact = keys.find((k) => normalize(k) === target);
      let chosen: string | undefined = exact;
      if (!chosen) {
        chosen = keys.find((k) => {
          const nk = normalize(k);
          return nk.includes(target) || target.includes(nk);
        });
      }

      setSubCategoryFilters(chosen ? [chosen] : [selectedSubcategory]);
    }
  }, [selectedSubcategory, hasUserAdjustedSubFilters, chartData]);

  // Hacky useEffect here
  useEffect(() => {
    if (fundIdCtx) {
      setFundFilters([fundIdCtx]);
    } else {
      // Reset fund filters if fundIdCtx is undefined
      setFundFilters(allFunds.map((f) => f.fund_name));
    }
  }, [fundIdCtx]);

  useEffect(() => {
    if (prevDataRef.current !== dataInDateRange) {
      prevDataRef.current = dataInDateRange;

      // Get all unique funds from the table data
      const allFunds = new Set<string>();
      const tableData = regulationData?.table_data ?? [];
      tableData.forEach((entry) => {
        allFunds.add(entry.fund_name);
      });

      // Get all unique subcategories from regulationData
      const allSubCategories = regulationData?.subcategories || [];

      // Only set filters if the user hasn't changed them
      setFundFilters((prev) =>
        prev.length === 0 ? Array.from(allFunds) : prev,
      );

      // Only set subcategory filters if no specific subcategory is selected and user hasn't adjusted
      if (!selectedSubcategory && !hasUserAdjustedSubFilters) {
        setSubCategoryFilters((prev) =>
          prev.length === 0 ? Array.from(allSubCategories) : prev,
        );
      }

      setSubCategoriesFails(Array.from(allSubCategories));
    }
  }, [
    dataInDateRange,
    selectedSubcategory,
    hasUserAdjustedSubFilters,
    regulationData,
  ]);

  const toggleStatus = (status: PieStatusKey) => {
    setStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  // Main filtering logic for chart
  const filteredChartData = useMemo(() => {
    return chartData.map((entry) =>
      processChartEntryFromChartData(
        entry,
        subCategoryFilters,
        fundFilters,
        searchTerm,
        statusFilters,
      ),
    );
  }, [chartData, subCategoryFilters, fundFilters, searchTerm, statusFilters]);

  // Derived table dataset that reflects selected subcategories
  const derivedTableData = useMemo(() => {
    const isAllOrEmpty =
      subCategoryFilters.length === 0 ||
      subCategoryFilters.length === subCategoriesFails.length;

    if (isAllOrEmpty) {
      const regulationData = Object.values(
        filteredData.regulation_breakdown,
      )[0];
      const tableData = regulationData?.table_data ?? [];
      return tableData.map((row) => ({
        ...row,
        regulation: regulation.title,
        regulation_id: regulation.id,
      }));
    }

    // Aggregate per fund using only selected subcategories from chartData
    const fundMap = new Map<
      string,
      {
        fund_id: string;
        fund_name: string;
        fund_name_full: string;
        fund_is_deceased: boolean;
        subcategories: Set<string>;
        fail_dates: Set<string>;
        alert_dates: Set<string>;
        pass_dates: Set<string>;
      }
    >();

    subCategoryFilters.forEach((subcat) => {
      chartData.forEach((point) => {
        const subcatData = point.data[subcat];
        if (!subcatData) return;

        (['Fail', 'Alert', 'Pass'] as const).forEach((statusType) => {
          const funds = subcatData[statusType]?.funds || [];
          funds.forEach((fund) => {
            // Use fund_name as the key to allow same fund_id with different fund_name
            const key = fund.fund_name;
            if (!fundMap.has(key)) {
              fundMap.set(key, {
                fund_id: fund.fund_id,
                fund_name: fund.fund_name,
                fund_name_full: fund.fund_name_full,
                fund_is_deceased: fund.fund_is_deceased,
                subcategories: new Set<string>(),
                fail_dates: new Set<string>(),
                alert_dates: new Set<string>(),
                pass_dates: new Set<string>(),
              });
            }
            const agg = fundMap.get(key)!;
            agg.subcategories.add(subcat);
            if (statusType === 'Fail') agg.fail_dates.add(point.date);
            if (statusType === 'Alert') agg.alert_dates.add(point.date);
            if (statusType === 'Pass') agg.pass_dates.add(point.date);
          });
        });
      });
    });

    const rows = Array.from(fundMap.values()).map((agg) => {
      const fail_count = agg.fail_dates.size;
      const alert_count = agg.alert_dates.size;
      const pass_count = agg.pass_dates.size;

      const allDates = new Set([
        ...agg.fail_dates,
        ...agg.alert_dates,
        ...agg.pass_dates,
      ]);
      const total_runs = allDates.size;
      const safeTotal = total_runs === 0 ? 1 : total_runs;

      return {
        fund_id: agg.fund_id,
        fund_name: agg.fund_name,
        fund_name_full: agg.fund_name_full,
        fund_is_deceased: agg.fund_is_deceased,
        subcategories: Array.from(agg.subcategories),
        regulation: regulation.title,
        regulation_id: regulation.id,
        fail_count,
        alert_count,
        pass_count,
        total_runs,
        total_checks: total_runs,
        // Percent values expected as decimals in [0,1]
        fail_rate_percent: fail_count / safeTotal,
        alert_rate_percent: alert_count / safeTotal,
        pass_rate_percent: pass_count / safeTotal,
        fail_dates: Array.from(agg.fail_dates),
        alert_dates: Array.from(agg.alert_dates),
        pass_dates: Array.from(agg.pass_dates),
      };
    });

    return rows;
  }, [
    chartData,
    subCategoryFilters,
    subCategoriesFails.length,
    filteredData.regulation_breakdown,
    regulation.id,
    regulation.title,
  ]);

  // This is for filters being global - Table includes pass rates
  const tableDataFiltered = useMemo(() => {
    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/gi, '');
    const normalizedFilterSet = new Set(subCategoryFilters.map(normalize));

    return derivedTableData.filter((entry) => {
      const fundMatch = fundFilters.includes(entry.fund_name);

      const subCategoryMatch =
        normalizedFilterSet.size === 0 ||
        entry.subcategories.some((sub) =>
          normalizedFilterSet.has(normalize(sub)),
        );

      const statusMatch =
        (entry.fail_count > 0 && statusFilters.includes('Fail')) ||
        (entry.alert_count > 0 && statusFilters.includes('Alert')) ||
        (entry.pass_count > 0 && statusFilters.includes('Pass'));

      return fundMatch && subCategoryMatch && statusMatch;
    });
  }, [
    derivedTableData,
    fundFilters,
    subCategoryFilters,
    statusFilters,
    searchTerm,
  ]);

  const isActiveRangeLoading = useIsActiveRangeLoading(
    activeCustomRange || null,
    sections,
  );

  // Check for loading of the currently active range (including preset ranges)
  const isActiveRangeDataLoading =
    useIsFetching({
      predicate: (query) => {
        const key = query.queryKey as readonly unknown[];
        if (
          !Array.isArray(key) ||
          key[0] !== 'dashboard_data' ||
          !key[1] ||
          !key[2]
        ) {
          return false;
        }

        const startDate = key[1] as string;
        const endDate = key[2] as string;

        // Check if this matches the current active range
        if (activeCustomRange) {
          return (
            startDate === activeCustomRange.startDate &&
            endDate === activeCustomRange.endDate
          );
        } else {
          // For preset ranges, calculate the expected dates
          const today = new Date().toISOString().split('T')[0];
          const numDays = daysInScope;
          const expectedStart =
            numDays === 1
              ? today
              : new Date(Date.now() - (numDays - 1) * 24 * 60 * 60 * 1000)
                  .toISOString()
                  .split('T')[0];

          return startDate === expectedStart && endDate === today;
        }
      },
    }) > 0;

  // Wrap setter to mark user-initiated changes so initial filter won't override
  const handleSetSubCategoryFilters: React.Dispatch<
    React.SetStateAction<string[]>
  > = (updater) => {
    setHasUserAdjustedSubFilters(true);
    setSubCategoryFilters(updater);
  };

  // Extract components to avoid duplication
  const chartComponent = (
    <ComplianceBarChart
      animationDelay={4.5}
      data={filteredChartData}
      regulation={regulation}
      allDates={allDates}
      subCategoryFilters={subCategoryFilters}
      daysInScope={daysInScope}
    />
  );

  const tableComponent = (
    <ComplianceTable
      data={tableDataFiltered}
      searchTerm={searchTerm}
      daysInScope={daysInScope}
      pageSize={pageSize}
    />
  );

  const categories = [
    {
      name: 'Chart',
      component: chartComponent,
      animation: slideInLeft,
    },
    {
      name: 'Table',
      component: tableComponent,
      animation: slideInRight,
    },
  ];

  return (
    <>
      <section className="block lg:hidden">
        <div className="relative">
          <TabGroup>
            <header className="flex w-full items-start justify-between">
              <TabList className="inline-flex h-fit rounded-full bg-gray-100 p-1">
                {categories.map(({ name }) => (
                  <Tab
                    key={name}
                    className="rounded-full px-4 py-1 text-sm font-semibold text-mainblue focus:outline-none focus:ring-2 focus:ring-blue-300 aria-selected:bg-white aria-selected:text-blue-700 aria-selected:shadow"
                  >
                    {name}
                  </Tab>
                ))}
              </TabList>

              <ChartToolbar
                setSearchTerm={setSearchTerm}
                fundFilters={fundFilters}
                setFundFilters={setFundFilters}
                subCategoryFilters={subCategoryFilters}
                setSubCategoryFilters={handleSetSubCategoryFilters}
                statusFilters={statusFilters}
                toggleStatus={toggleStatus}
                allFunds={allFunds.map((f) => f.fund_name)}
                subCategoriesStatus={subCategoriesFails}
                regulation={regulation}
                data={tableDataFiltered}
              />
            </header>

            <TabPanels className="mt-3 min-h-[200px]">
              {categories.map(({ name, component, animation }) => (
                <TabPanel key={name}>
                  <motion.div key={name} {...animation}>
                    {component}
                  </motion.div>
                </TabPanel>
              ))}
            </TabPanels>
          </TabGroup>

          {(isActiveRangeLoading || isActiveRangeDataLoading) && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <LoadingSpinner className="h-6 w-6 border-2" />
                <span className="text-sm text-gray-600">
                  Loading {entityLabel} data...
                </span>
              </div>
            </div>
          )}
        </div>
      </section>
      <section className="hidden h-full lg:block">
        <div className="relative flex h-full w-full flex-col">
          <ChartToolbar
            setSearchTerm={setSearchTerm}
            fundFilters={fundFilters}
            setFundFilters={setFundFilters}
            subCategoryFilters={subCategoryFilters}
            setSubCategoryFilters={handleSetSubCategoryFilters}
            statusFilters={statusFilters}
            toggleStatus={toggleStatus}
            allFunds={allFunds.map((f) => f.fund_name)}
            subCategoriesStatus={subCategoriesFails}
            regulation={regulation}
            data={tableDataFiltered}
          />

          <div className="flex h-full flex-col">
            {chartComponent}
            <div className="h-4" />
            <div className="flex-1">{tableComponent}</div>
          </div>

          {(isActiveRangeLoading || isActiveRangeDataLoading) && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <LoadingSpinner className="h-6 w-6 border-2" />
                <span className="text-sm text-gray-600">
                  Loading {entityLabel} data...
                </span>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

const ChartViewSection: FunctionComponent<ChartViewSectionProps> = (props) => {
  return (
    <ChartViewProvider>
      <ChartViewSectionComponent {...props} />
    </ChartViewProvider>
  );
};

export default ChartViewSection;
